import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Tooltip, IconButton, Typography, Menu, MenuItem, Badge, useMediaQuery } from '@mui/material';
import { Home, LayoutDashboard, Radio, Anvil, Box as BoxIcon, MoreVertical } from 'lucide-react';
import { MODULE_LIST, type ModuleId } from '../theme/moduleColors';

const ICON_MAP: Record<string, React.ReactElement> = {
  Box: <BoxIcon size={16} />,
};

// DEC-48-8: first 6 tabs inline at <=768px; remaining 5 in overflow menu
const INLINE_TAB_COUNT = 6;

interface ModuleNavBarProps {
  activeModule: ModuleId | null;
}

const ModuleNavBar: React.FC<ModuleNavBarProps> = ({ activeModule }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const isHome = location.pathname === '/';
  const isDashboard = location.pathname.startsWith('/dashboard');
  // Custom 768px breakpoint (between MUI sm=600 and md=900)
  const isNarrow = useMediaQuery('(max-width: 768px)');

  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);

  const inlineMods = isNarrow ? MODULE_LIST.slice(0, INLINE_TAB_COUNT) : MODULE_LIST;
  const overflowMods = isNarrow ? MODULE_LIST.slice(INLINE_TAB_COUNT) : [];
  const overflowActive = isNarrow && overflowMods.some((m) => m.id === activeModule);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', overflowX: 'auto', minHeight: 48 }}>
      <Tooltip title="Home">
        <IconButton size="small" onClick={() => navigate('/')} sx={{ borderRadius: 1, bgcolor: isHome ? 'rgba(11,37,69,0.08)' : 'transparent', color: isHome ? 'primary.main' : 'text.secondary', '&:hover': { bgcolor: isHome ? 'rgba(11,37,69,0.12)' : 'rgba(11,37,69,0.04)' } }}>
          <Home size={16} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Dashboard">
        <IconButton size="small" onClick={() => navigate('/dashboard')} sx={{ borderRadius: 1, bgcolor: isDashboard ? 'rgba(11,37,69,0.08)' : 'transparent', color: isDashboard ? 'primary.main' : 'text.secondary', '&:hover': { bgcolor: isDashboard ? 'rgba(11,37,69,0.12)' : 'rgba(11,37,69,0.04)' } }}>
          <LayoutDashboard size={16} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Event Monitor">
        <IconButton size="small" onClick={() => navigate('/events')} sx={{ borderRadius: 1, bgcolor: location.pathname.startsWith('/events') ? 'rgba(255, 87, 34, 0.08)' : 'transparent', color: location.pathname.startsWith('/events') ? '#D9533A' : 'text.secondary', '&:hover': { bgcolor: location.pathname.startsWith('/events') ? 'rgba(255, 87, 34, 0.12)' : 'rgba(255, 87, 34, 0.04)' } }}>
          <Radio size={16} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Forge Admin">
        <IconButton size="small" onClick={() => navigate('/forge/admin')} sx={{ borderRadius: 1, bgcolor: location.pathname.startsWith('/forge') ? 'rgba(199, 122, 31, 0.08)' : 'transparent', color: location.pathname.startsWith('/forge') ? 'secondary.main' : 'text.secondary', '&:hover': { bgcolor: location.pathname.startsWith('/forge') ? 'rgba(199, 122, 31, 0.12)' : 'rgba(199, 122, 31, 0.04)' } }}>
          <Anvil size={16} />
        </IconButton>
      </Tooltip>
      <Box sx={{ width: '1px', height: 24, bgcolor: 'divider', mx: 0.5, flexShrink: 0 }} />

      {/* Inline tabs (all 11 at >768px; first 6 at <=768px) */}
      {inlineMods.map((mod) => {
        const isActive = activeModule === mod.id;
        const label = t(`modules.${mod.id}.label`, { defaultValue: mod.label });
        return (
          <Tooltip key={mod.id} title={label}>
            <Box
              onClick={() => navigate(`/${mod.id}`)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1.5, py: 0.5, borderRadius: 1, cursor: 'pointer',
                minHeight: 44,
                bgcolor: isActive ? `${mod.accent}1F` : 'transparent',
                border: isActive ? `1px solid ${mod.accent}66` : '1px solid transparent',
                color: isActive ? mod.accent : 'text.secondary',
                '&:hover': { bgcolor: `${mod.accent}14`, color: mod.accent },
                transition: 'all 0.15s', whiteSpace: 'nowrap',
              }}
            >
              {ICON_MAP[mod.icon] || <Home size={16} />}
              <Typography
                variant="caption"
                fontWeight={isActive ? 600 : 400}
                title={label}
                sx={{ fontSize: '0.75rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {label}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}

      {/* Overflow "More" button — only at <=768px when overflow tabs exist (DEC-48-8) */}
      {isNarrow && overflowMods.length > 0 && (
        <>
          <Tooltip title={t('nav.more', 'More')}>
            <IconButton
              size="small"
              onClick={(e) => setMoreAnchor(e.currentTarget)}
              aria-label="More navigation options"
              sx={{
                minHeight: 44,
                borderRadius: 1,
                bgcolor: overflowActive ? 'rgba(11,37,69,0.08)' : 'transparent',
                color: overflowActive ? 'primary.main' : 'text.secondary',
                '&:hover': { bgcolor: 'rgba(11,37,69,0.04)' },
              }}
            >
              <Badge
                variant="dot"
                invisible={!overflowActive}
                color="primary"
                sx={{ '& .MuiBadge-dot': { top: 4, right: 4 } }}
              >
                <MoreVertical size={18} />
              </Badge>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={moreAnchor}
            open={Boolean(moreAnchor)}
            onClose={() => setMoreAnchor(null)}
            PaperProps={{ sx: { minWidth: 180, mt: 0.5 } }}
          >
            {overflowMods.map((mod) => {
              const isActive = activeModule === mod.id;
              const label = t(`modules.${mod.id}.label`, { defaultValue: mod.label });
              return (
                <MenuItem
                  key={mod.id}
                  selected={isActive}
                  onClick={() => {
                    setMoreAnchor(null);
                    navigate(`/${mod.id}`);
                  }}
                  sx={{
                    gap: 1, minHeight: 44,
                    color: isActive ? mod.accent : 'text.primary',
                    fontWeight: isActive ? 600 : 400,
                    '&.Mui-selected': { bgcolor: `${mod.accent}14` },
                  }}
                >
                  {ICON_MAP[mod.icon] || <Home size={16} />}
                  <Typography variant="body2" fontWeight={isActive ? 600 : 400}>{label}</Typography>
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default ModuleNavBar;
